import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { callList } from '@/lib/rpc/facade';
import type { ListResult } from '@/lib/rpc/types';

export interface CustodialWallet {
  id: string;
  bridge_wallet_id: string;
  chain: string;
  address: string;
  status: string;
  label?: string | null;
  created_at: string;
  updated_at: string;
}

/**
 * Hook to fetch user's custodial wallets
 * Uses get_my_wallet_balances RPC which returns wallets with balances
 */
export function useCustodialWallets() {
  const [wallets, setWallets] = useState<CustodialWallet[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const fetchWallets = async () => {
    setLoading(true);
    setError(null);

    try {
      // Use get_my_wallet_balances RPC which returns wallets with balances
      const { data, error: rpcError } = await supabase.rpc('get_my_wallet_balances');

      if (rpcError) {
        throw new Error(rpcError.message || 'Failed to fetch wallets');
      }

      // Parse response - RPC returns { success: true, wallets: [...] }
      let walletsData: any[] = [];
      if (data && typeof data === 'object') {
        if (data.success && Array.isArray(data.wallets)) {
          walletsData = data.wallets;
        } else if (Array.isArray(data)) {
          walletsData = data;
        }
      }
      
      // Map to CustodialWallet format
      const mappedWallets: CustodialWallet[] = walletsData.map((w: any) => ({
        id: w.id || w.wallet_id,
        bridge_wallet_id: w.bridge_wallet_id || '', // Now returned by updated RPC
        chain: w.chain || '',
        address: w.address || '',
        status: w.status || 'active',
        label: w.label || null,
        created_at: w.created_at || new Date().toISOString(),
        updated_at: w.updated_at || new Date().toISOString(),
      }));

      // Filter only active wallets
      const activeWallets = mappedWallets.filter(w => w.status === 'active' && w.address);
      
      setWallets(activeWallets);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Failed to fetch wallets'));
      setWallets([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchWallets();
  }, []);

  return {
    wallets,
    loading,
    error,
    refetch: fetchWallets,
  };
}

